import React, { useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
  message,
} from "antd";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import get from "lodash/get";
import { postApi } from "../../redux/rest/restSlice";
import {
  CREATE_COUPON_CODE,
  UPDATE_COUPON_CODE,
} from "../../constants/actionTypes";
import { COUPON_TYPES } from "../../utilities/helper";

const { Option } = Select;

const range = (start, end) =>
  Array.from({ length: Math.max(end - start, 0) }, (_, i) => start + i);

// Expiry must be in the future: block past days, and past hours/minutes of today
const disabledExpiryDate = (current) =>
  current && current < dayjs().startOf("day");

const disabledExpiryTime = (current) => {
  const now = dayjs();
  if (!(current || now).isSame(now, "day")) return {};

  return {
    disabledHours: () => range(0, now.hour()),
    disabledMinutes: (selectedHour) =>
      selectedHour === now.hour() ? range(0, now.minute() + 1) : [],
  };
};

const AddEditCouponCode = ({ modalInfo, setModalInfo, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const record = get(modalInfo, "data");
  const isEdit = !!record;

  const type = Form.useWatch("type", form);
  const isNeverExpire = Form.useWatch("is_never_expire", form);

  const initialValues = isEdit
    ? {
        name: record.name,
        coupon_code: record.coupon_code,
        type: record.type,
        flat_off:
          record.flat_off !== null && record.flat_off !== undefined
            ? String(record.flat_off)
            : null,
        percentage_off:
          record.percentage_off !== null &&
          record.percentage_off !== undefined
            ? String(record.percentage_off)
            : null,
        is_multi_use: !!record.is_multi_use,
        is_active: !!record.is_active,
        is_never_expire: !!record.is_never_expire,
        expiry_time: record.expiry_time_in_ms
          ? dayjs(Number(record.expiry_time_in_ms))
          : null,
      }
    : {
        type: "FLAT_OFF",
        is_multi_use: true,
        is_active: true,
        is_never_expire: false,
      };

  const onFinish = (values) => {
    const isFlatOff = values.type === "FLAT_OFF";

    const payload = {
      coupon_code: values.coupon_code.trim(),
      name: values.name.trim(),
      is_multi_use: !!values.is_multi_use,
      is_active: !!values.is_active,
      is_never_expire: !!values.is_never_expire,
      type: values.type,
      flat_off: isFlatOff ? String(values.flat_off) : null,
      percentage_off: isFlatOff ? null : String(values.percentage_off),
      expiry_time_in_ms: values.is_never_expire
        ? null
        : String(values.expiry_time.valueOf()),
    };

    if (isEdit) {
      payload.id = record.id;
    }

    setLoading(true);
    dispatch(
      postApi({
        endpoint: isEdit ? UPDATE_COUPON_CODE : CREATE_COUPON_CODE,
        payload,
      })
    )
      .then(unwrapResult)
      .then(() => {
        setLoading(false);
        message.success(
          isEdit
            ? "Coupon Code Updated Successfully !!"
            : "Coupon Code Created Successfully !!"
        );
        onSuccess();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        name="coupon-code"
        layout="vertical"
        initialValues={initialValues}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, whitespace: true, message: "Please enter the name" },
          ]}
        >
          <Input placeholder="Enter Name (e.g. Flat 5 OFF)" />
        </Form.Item>

        <Form.Item
          label="Coupon Code"
          name="coupon_code"
          rules={[
            { required: true, whitespace: true, message: "Please enter the coupon code" },
            { min: 6, message: "Coupon code must be at least 6 characters" },
          ]}
        >
          <Input placeholder="Enter Coupon Code (e.g. FLAT5OFF)" />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Please select a type" }]}
        >
          <Select placeholder="Select Type">
            {Object.entries(COUPON_TYPES).map(([key, label]) => (
              <Option key={key} value={key}>
                {label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {type === "FLAT_OFF" && (
          <Form.Item
            label="Flat Off ($)"
            name="flat_off"
            rules={[
              { required: true, message: "Please enter the flat off amount" },
              {
                validator: (_, value) =>
                  value === null || value === undefined || Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject("Amount must be greater than 0"),
              },
            ]}
          >
            <InputNumber
              stringMode
              placeholder="Enter Flat Off Amount"
              min={0}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}

        {type === "PERCENTAGE_OFF" && (
          <Form.Item
            label="Percentage Off (%)"
            name="percentage_off"
            rules={[
              { required: true, message: "Please enter the percentage off" },
              {
                validator: (_, value) =>
                  value === null ||
                  value === undefined ||
                  (Number(value) > 0 && Number(value) <= 100)
                    ? Promise.resolve()
                    : Promise.reject("Percentage must be between 0 and 100"),
              },
            ]}
          >
            <InputNumber
              stringMode
              placeholder="Enter Percentage Off"
              min={0}
              max={100}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}

        <Form.Item label="Multi Use" name="is_multi_use" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          label="Never Expire"
          name="is_never_expire"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        {!isNeverExpire && (
          <Form.Item
            label="Expiry Time"
            name="expiry_time"
            rules={[
              { required: true, message: "Please pick the expiry time" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allow an existing expiry to stay as-is while editing other fields
                  if (
                    isEdit &&
                    record.expiry_time_in_ms &&
                    value.valueOf() === Number(record.expiry_time_in_ms)
                  ) {
                    return Promise.resolve();
                  }
                  return value.valueOf() > Date.now()
                    ? Promise.resolve()
                    : Promise.reject("Expiry time must be in the future");
                },
              },
            ]}
          >
            <DatePicker
              showTime
              disabledDate={disabledExpiryDate}
              disabledTime={disabledExpiryTime}
              format="DD/MM/YYYY hh:mm A"
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}

        <div className="mt-20 d-flex cabinet-btn">
          <Form.Item>
            <Button
              className="default-secondary-btn"
              onClick={() => {
                setModalInfo();
              }}
            >
              Cancel
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="default-primary-btn"
            >
              {isEdit ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </Spin>
  );
};

export default AddEditCouponCode;
